import type { CampaignTarget } from "@adpilot/shared";
import {
  AD_GROUP_COLUMNS,
  csvDocument,
  formatGroupMetric,
  type GroupColumn,
  groupMetric,
} from "../ad-groups/ad-groups-table-model";

/** Reference sub tabs: 关键词 / 商品投放 / 自动投放. Theme targets are keyword groups; category targets are products. */
export const TARGET_SUB_TABS = ["keyword", "product", "auto"] as const;
export type TargetSubTab = (typeof TARGET_SUB_TABS)[number];
export function targetSubTab(target: Pick<CampaignTarget, "targetType">): TargetSubTab {
  if (target.targetType === "keyword" || target.targetType === "theme") return "keyword";
  if (target.targetType === "auto") return "auto";
  return "product";
}

/** Metric columns shared with the Ad Groups table (same labels, same formatting). */
export type TargetMetricColumn = Exclude<GroupColumn, "defaultBid" | "status">;
export const TARGET_METRIC_COLUMNS = AD_GROUP_COLUMNS.filter(
  (key): key is TargetMetricColumn => key !== "defaultBid" && key !== "status",
);
export const TARGET_IDENTITY_COLUMNS = ["status", "adGroup", "bid", "tags"] as const;
export const TARGET_COLUMNS = [
  ...TARGET_IDENTITY_COLUMNS,
  ...TARGET_METRIC_COLUMNS,
  "amazonCreatedAt",
] as const;
export type TargetColumn = (typeof TARGET_COLUMNS)[number];
export type TargetSortKey = TargetColumn | "name";
export const TARGET_FIXED_COLUMNS = ["active", "name"] as const;

export const isTargetMetricColumn = (key: TargetColumn): key is TargetMetricColumn =>
  (TARGET_METRIC_COLUMNS as readonly string[]).includes(key);

/** Sort/compare value for a column; null sorts last. */
export function targetSortValue(row: CampaignTarget, key: TargetSortKey): number | string | null {
  switch (key) {
    case "name":
      return row.label;
    case "status":
      return row.deliveryStatus;
    case "adGroup":
      return row.adGroup;
    case "bid":
      return row.bid;
    case "tags":
      return row.tags.length ? row.tags.map((tag) => tag.tag).join(", ") : null;
    case "amazonCreatedAt":
      return row.amazonCreatedAt;
    default:
      return groupMetric(row, key);
  }
}

export function sortTargets(
  rows: readonly CampaignTarget[],
  key: TargetSortKey,
  direction: "asc" | "desc",
): CampaignTarget[] {
  return [...rows].sort((a, b) => {
    const x = targetSortValue(a, key);
    const y = targetSortValue(b, key);
    if (x === null) return y === null ? 0 : 1;
    if (y === null) return -1;
    return (
      (typeof x === "string" && typeof y === "string"
        ? x.localeCompare(y)
        : Number(x) - Number(y)) * (direction === "asc" ? 1 : -1)
    );
  });
}

export interface TargetCellText {
  /** Copy for values AdPraxis cannot supply, e.g. a bid inherited from the ad group. */
  bidDefault: string;
  /** Localised Amazon delivery status; falls back to the raw value for unknown codes. */
  deliveryStatus: (raw: string) => string;
}

export function formatTargetCell(
  row: CampaignTarget,
  key: TargetColumn,
  currency: string | null,
  locale: string,
  text: TargetCellText,
): string {
  switch (key) {
    case "status":
      return row.deliveryStatus ? text.deliveryStatus(row.deliveryStatus) : "—";
    case "adGroup":
      return row.adGroup;
    case "bid":
      return row.bid === null
        ? text.bidDefault
        : formatGroupMetric(row.bid, "spend", currency, locale);
    case "tags":
      return row.tags.length ? row.tags.map((tag) => tag.tag).join(", ") : "—";
    case "amazonCreatedAt":
      return row.amazonCreatedAt
        ? new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
            new Date(row.amazonCreatedAt),
          )
        : "—";
    default:
      return formatGroupMetric(groupMetric(row, key), key, currency, locale);
  }
}

export function exportTargetsCsv(
  rows: readonly CampaignTarget[],
  columns: readonly TargetSortKey[],
  label: (key: TargetSortKey) => string,
  currency: string | null,
  locale: string,
  text: TargetCellText,
): string {
  return csvDocument([
    columns.map(label),
    ...rows.map((row) =>
      columns.map((key) =>
        key === "name" ? row.label : formatTargetCell(row, key, currency, locale, text),
      ),
    ),
  ]);
}
